import { useEffect, useRef, useState } from 'react'
import { useQuery, useQueryClient } from '@tanstack/react-query'
import { useTranslation } from 'react-i18next'
import { toast } from 'sonner'
import type { Product } from '@/types/products'
import { startProductSync, getProduct } from '@/lib/api/products'
import { isMasterDataStale, syncedAtAdvanced } from '@/lib/products/syncFreshness'

const POLL_INTERVAL_MS = 3_000
const MAX_POLLS = 10
const INVALIDATE_KEYS = ['product', 'product-assets', 'product-images', 'product-content-blocks']

/**
 * Synct Stammdaten/Preise/raw_data (Kategorien/Attribute/Lager) beim Öffnen,
 * wenn synced_at älter als 15 Min ist. Erkennt den Abschluss über synced_at-Polling
 * und zeigt dann einen dezenten Toast. Fehler/Timeout sind still.
 * Gibt zurück, ob der Sync gerade läuft (für den Sync-Indikator im Hero).
 */
export function useMasterDataSync(product: Product | undefined): boolean {
  const queryClient = useQueryClient()
  const { t } = useTranslation()
  const triggeredRef = useRef(false)
  const baselineRef = useRef<string | null>(null)
  const pollsRef = useRef(0)
  const [polling, setPolling] = useState(false)
  const [active, setActive] = useState(false)
  const productId = product?.id

  // StrictMode-Doppelmount kann den Sync in DEV zweimal feuern — bewusst akzeptiert (serverseitig idempotent).
  useEffect(() => {
    if (!product || triggeredRef.current || !isMasterDataStale(product.synced_at)) return
    triggeredRef.current = true
    baselineRef.current = product.synced_at ?? null
    pollsRef.current = 0
    setActive(true)
    startProductSync({ provider: 'jtl', full_sync: false, product_id: product.id })
      .then(() => setPolling(true))
      .catch(() => setActive(false))
  }, [product])

  const poll = useQuery({
    queryKey: ['master-data-sync-poll', productId],
    queryFn: () => getProduct(productId as string),
    enabled: polling && !!productId,
    refetchInterval: polling ? POLL_INTERVAL_MS : false,
    gcTime: 0,
  })

  useEffect(() => {
    if (!polling || !poll.data?.product || !productId) return
    pollsRef.current += 1
    if (syncedAtAdvanced(baselineRef.current, poll.data.product.synced_at)) {
      setPolling(false)
      setActive(false)
      INVALIDATE_KEYS.forEach((k) => queryClient.invalidateQueries({ queryKey: [k, productId] }))
      toast.success(t('toast.success.productDataUpdated'))
    } else if (pollsRef.current >= MAX_POLLS) {
      setPolling(false)
      setActive(false)
    }
  }, [poll.data, polling, productId, queryClient, t])

  return active
}
